import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  JURISDICTION_LABELS,
  taxKindForService,
  taxPeriodAnchorSupported,
  ukTemplateKind,
  ukTemplateDates,
  ukTemplateDateHint,
  type ApplyTemplateOutput,
  type RecurrenceFrequency,
  type TemplateEvidence,
  type TemplateOutput,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { SearchableSelect } from './SearchableSelect.js';
import { DateInput, validateDateInputs } from './DateInput.js';
import { fmtDate } from '../lib/date.js';

const FREQ_LABELS: Record<RecurrenceFrequency, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-annual',
  ANNUAL: 'Annual',
};

/** Which vault document backs a template item, if any. */
type EvidenceKind = 'vat' | 'corporateTax' | 'tradeLicense' | null;

function classifyItem(serviceType: string, title: string, jurisdiction: string): EvidenceKind {
  const tax = taxKindForService(serviceType, jurisdiction);
  if (tax === 'VAT') return 'vat';
  if (tax === 'CT') return 'corporateTax';
  if (/license/i.test(serviceType) || /license/i.test(title)) return 'tradeLicense';
  return null;
}

/** The deadline the document implies for this item kind (TL: renew by expiry). */
function evidenceDeadline(evidence: TemplateEvidence, kind: EvidenceKind): string | null {
  if (kind === 'vat') return evidence.vat?.deadline ?? null;
  if (kind === 'corporateTax') return evidence.corporateTax?.deadline ?? null;
  if (kind === 'tradeLicense') return evidence.tradeLicense?.expiryDate ?? null;
  return null;
}

const EVIDENCE_DOC_LABELS: Record<Exclude<EvidenceKind, null>, string> = {
  vat: 'VAT certificate',
  corporateTax: 'Corporate tax certificate',
  tradeLicense: 'Trade license',
};

/**
 * "Apply template" modal — pick an active engagement template, tick the items
 * to spawn (defaultIncluded ones start checked), optionally adjust each item's
 * first End/Due dates, confirm, see created/skipped.
 *
 * UAE tax items use reviewed certificate dates. Mapped UK services use the
 * published registry pairs and the firm's internal HMRC completion target.
 * Unknown source dates stay blank; unchanged refetches preserve manual edits.
 */
export function ApplyTemplateModal({ clientId, engagementId, jurisdiction, onClose, onDone, skipLabel, retainerFork = false }: {
  clientId: string;
  engagementId?: string;
  retainerFork?: boolean;
  /**
   * OPS 3.5 C: jurisdiction-driven catalog — when the caller knows the
   * governing jurisdiction (engagement detail: engagement.jurisdiction wins),
   * pass it; otherwise the modal resolves the client's own jurisdiction.
   * Only that jurisdiction's templates are listed (a "show all" toggle
   * remains as an escape hatch).
   */
  jurisdiction?: string | null;
  onClose: () => void;
  onDone: (result: ApplyTemplateOutput) => void;
  /** When set (the post-client-creation prompt), a tertiary Skip button is
   *  shown alongside Apply — same effect as closing, but explicit. */
  skipLabel?: string;
}) {
  const [templateId, setTemplateId] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [dates, setDates] = useState<Record<string, string>>({});
  const [ends, setEnds] = useState<Record<string, string>>({});
  const [dateRevisions, setDateRevisions] = useState<Record<string, number>>({});
  const [manualDates, setManualDates] = useState<Record<string, boolean>>({});
  const [sourceIds, setSourceIds] = useState<Record<string, string>>({});
  const initialized = useRef('');
  const [showAllJurisdictions, setShowAllJurisdictions] = useState(false);

  const clientJurisdiction = useQuery({
    queryKey: ['client-jurisdiction', clientId],
    enabled: jurisdiction === undefined,
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${clientId}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { jurisdiction: string | null } };
      return body.data.jurisdiction;
    },
  });
  const jur = jurisdiction !== undefined ? jurisdiction : clientJurisdiction.data ?? null;

  const templates = useQuery({
    queryKey: ['templates', 'active'],
    queryFn: async () => {
      const res = await fetch('/v1/templates?active=true', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TemplateOutput[] } };
      return body.data.items;
    },
  });

  const allTemplates = templates.data ?? [];
  const visibleTemplates = !jur || showAllJurisdictions
    ? allTemplates
    : allTemplates.filter((t) => t.jurisdiction === jur);
  const jurLabel = jur ? (JURISDICTION_LABELS[jur as keyof typeof JURISDICTION_LABELS] ?? jur) : null;

  const evidence = useQuery({
    queryKey: ['template-evidence', clientId],
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${clientId}/template-evidence`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: TemplateEvidence };
      return body.data;
    },
  });

  const selected = allTemplates.find((t) => t.id === templateId) ?? null;

  useEffect(() => {
    if (!selected) { initialized.current = ''; setChecked({}); setDates({}); setSourceIds({}); setManualDates({}); return; }
    const key = `${clientId}:${selected.id}`;
    if (initialized.current === key) return;
    const ev = evidence.data ?? null;
    if (!ev && !evidence.isError) return;
    initialized.current = key;
    setManualDates({});
    setEnds(Object.fromEntries(selected.items.map((i) => {
      const kind = classifyItem(i.serviceType, i.title, selected.jurisdiction);
      const uk = ukTemplateDates(ev?.ukRegistry, i.serviceType, selected.jurisdiction);
      return [i.id, uk?.endDate ?? (kind === 'vat' ? ev?.vat?.firstPeriodEndDate ?? '' : kind === 'corporateTax' ? ev?.corporateTax?.firstPeriodEndDate ?? '' : '')];
    })));
    setChecked(Object.fromEntries(selected.items.map((i) => {
      const kind = classifyItem(i.serviceType, i.title, selected.jurisdiction);
      if (kind === 'vat' || kind === 'corporateTax') return [i.id, !!(ev && evidenceDeadline(ev, kind))];
      if (kind) return [i.id, ev !== null && ev[kind] !== null];
      return [i.id, i.defaultIncluded];
    })));
    setDates(Object.fromEntries(selected.items.map((i) => {
      const kind = classifyItem(i.serviceType, i.title, selected.jurisdiction);
      const ukKind = ukTemplateKind(i.serviceType, selected.jurisdiction), uk = ukTemplateDates(ev?.ukRegistry, i.serviceType, selected.jurisdiction);
      const deadline = ev && kind ? evidenceDeadline(ev, kind) : null;
      return [i.id, (ukKind ? uk?.dueDate ?? '' : deadline ?? (kind === 'vat' || kind === 'corporateTax' ? '' : i.firstDeadlineDefault)).slice(0, 10)];
    })));
    setSourceIds(Object.fromEntries(selected.items.flatMap((i) => {
      const kind = classifyItem(i.serviceType, i.title, selected.jurisdiction), doc = ev && kind ? ev[kind] : null;
      if (ukTemplateDates(ev?.ukRegistry, i.serviceType, selected.jurisdiction) && ev?.ukRegistry) return [[i.id, ev.ukRegistry.companyNumber]];
      return (kind === 'vat' || kind === 'corporateTax') && doc && ev && evidenceDeadline(ev, kind) ? [[i.id, doc.documentId]] : [];
    })));
  }, [clientId, selected?.id, evidence.data, evidence.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkedCount = selected ? selected.items.filter((i) => checked[i.id]).length : 0;
  const missingTaxDeadline = selected?.items.some((i) => checked[i.id] && taxKindForService(i.serviceType, selected.jurisdiction) && !dates[i.id]) ?? false;

  const apply = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/templates/${templateId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({
          clientId,
          ...(engagementId ? { engagementId } : {}),
          ...(retainerFork ? { retainerFork: true } : {}),
          itemOverrides: (selected?.items ?? []).map((i) => ({
            templateItemId: i.id,
            include: !!checked[i.id],
            ...(taxKindForService(i.serviceType, selected!.jurisdiction) || ukTemplateKind(i.serviceType, selected!.jurisdiction) ? { firstEndDate: ends[i.id] ? `${ends[i.id]}T00:00:00.000Z` : null, firstDueDate: dates[i.id] ? `${dates[i.id]}T00:00:00.000Z` : null } : dates[i.id] ? { firstEndDate: `${dates[i.id]}T00:00:00.000Z` } : {}),
            ...(!manualDates[i.id] && sourceIds[i.id] ? ukTemplateKind(i.serviceType, selected!.jurisdiction) ? { registryEvidenceCompanyNumber: sourceIds[i.id] } : { evidenceDocumentId: sourceIds[i.id] } : {}),
          })),
        }),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ApplyTemplateOutput };
      return body.data;
    },
    onSuccess: onDone,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div data-date-form className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Apply template</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>

        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs text-muted">Template{jurLabel && !showAllJurisdictions ? ` (${jurLabel})` : ''}</label>
          {jur && (
            <label className="flex items-center gap-1 text-[10px] text-muted cursor-pointer" title="Show templates from every jurisdiction">
              <input
                type="checkbox"
                checked={showAllJurisdictions}
                onChange={(e) => setShowAllJurisdictions(e.target.checked)}
                className="accent-emerald-400"
              />
              All jurisdictions
            </label>
          )}
        </div>
        {jur && !showAllJurisdictions && visibleTemplates.length === 0 && !templates.isLoading ? (
          <p className="text-xs text-amber-300/90 bg-bg-base border border-border rounded-lg px-3 py-2">
            No templates for {jurLabel} yet — create one under Admin → Compliance Templates.
          </p>
        ) : (
          <SearchableSelect
            value={templateId}
            onChange={setTemplateId}
            placeholder="Select a template…"
            options={visibleTemplates.map((t) => ({ value: t.id, label: t.name }))}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            wrapperClassName="w-full"
          />
        )}

        {selected && (
          <div className="mt-4">
            <p className="text-xs text-muted mb-2">
              {JURISDICTION_LABELS[selected.jurisdiction as keyof typeof JURISDICTION_LABELS] ?? selected.jurisdiction}
              {' · '}{checkedCount} of {selected.items.length} items selected (existing series are skipped)
              {' · '}End is the actual period end; Due is the filing/service deadline:
            </p>
            <div className="bg-bg-base border border-border rounded-lg divide-y divide-border/50">
              {selected.items.map((i) => {
                const kind = classifyItem(i.serviceType, i.title, selected.jurisdiction);
                const doc = kind && evidence.data ? evidence.data[kind] : null;
                const docDate = kind && evidence.data ? evidenceDeadline(evidence.data, kind) : null;
                const taxDoc = kind === 'vat' ? evidence.data?.vat : kind === 'corporateTax' ? evidence.data?.corporateTax : null;
                const tax = kind === 'vat' || kind === 'corporateTax';
                const ukKind = ukTemplateKind(i.serviceType, selected.jurisdiction);
                const uk = ukTemplateDates(evidence.data?.ukRegistry, i.serviceType, selected.jurisdiction);
                const dualDates = tax || !!ukKind;
                const anchored = taxDoc && taxPeriodAnchorSupported(kind === 'vat' ? 'VAT' : 'CT', taxDoc.taxPeriodPattern, taxDoc.firstPeriodStartDate, taxDoc.firstPeriodEndDate, i.recurrenceFrequency);
                return (
                  <div key={i.id} className="px-3 py-2 flex flex-wrap items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!checked[i.id]}
                      onChange={(e) => setChecked((c) => ({ ...c, [i.id]: e.target.checked }))}
                      className="accent-emerald-400 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{i.title}</div>
                      <div className="text-xs text-muted truncate">{i.serviceType} · {FREQ_LABELS[i.recurrenceFrequency]}</div>
                      {kind && evidence.data && doc && (
                        <div className="text-xs text-emerald-300/90 truncate">
                          ✓ {EVIDENCE_DOC_LABELS[kind]} on file{docDate ? ` — ${fmtDate(docDate)}` : ''}
                        </div>
                      )}
                      {kind && evidence.data && !doc && <div className="text-xs text-amber-300/90">No {EVIDENCE_DOC_LABELS[kind].toLowerCase()} on file.</div>}
                      {tax && <div className="text-xs space-y-1 mt-1 break-words">
                        <p className="text-muted">Period: {taxDoc?.firstPeriodStartDate ? fmtDate(taxDoc.firstPeriodStartDate) : 'unknown'} → {taxDoc?.firstPeriodEndDate ? fmtDate(taxDoc.firstPeriodEndDate) : 'unknown'} · {taxDoc?.taxPeriodPattern ?? 'cycle unknown'}</p>
                        <p className="text-muted">Deadline source: {manualDates[i.id] || !sourceIds[i.id] ? 'MANUAL' : taxDoc?.deadlineSource ?? 'UNKNOWN'}. {anchored && !manualDates[i.id] ? 'Reviewed period-based recurrence.' : 'Manual cadence; certificate period/cycle is not an automatic anchor.'}</p>
                        {!docDate && <p className="text-amber-300">No usable certificate deadline. Leave unticked, or explicitly enter a manual date.</p>}
                        {dates[i.id] && dates[i.id]! < new Date().toISOString().slice(0, 10) && <p className="text-amber-300">Past first deadline is retained; no future roll or filed/completed assumption.</p>}
                        {(taxDoc?.warnings ?? []).map((warning) => <p key={warning} className="text-amber-300">{warning}</p>)}
                        {sourceIds[i.id] && taxDoc?.documentId !== sourceIds[i.id] && !manualDates[i.id] && <p className="text-amber-300">Certificate source changed. Review the current source before applying; your edits have been kept.</p>}
                        {taxDoc?.deadline && <button type="button" className="text-accent underline" onClick={() => { setDates((state) => ({ ...state, [i.id]: taxDoc.deadline! })); setEnds((state) => ({ ...state, [i.id]: taxDoc.firstPeriodEndDate ?? '' })); setManualDates((state) => ({ ...state, [i.id]: false })); setSourceIds((state) => ({ ...state, [i.id]: taxDoc.documentId })); setDateRevisions((state) => ({ ...state, [i.id]: (state[i.id] ?? 0) + 1 })); }}>Use reviewed certificate dates</button>}
                      </div>}
                      {ukKind && <div className="text-xs space-y-1 mt-1 break-words">
                        <p className="text-muted">{ukTemplateDateHint(i.serviceType, selected.jurisdiction)}</p>
                        <p className="text-muted">Date source: {manualDates[i.id] || !sourceIds[i.id] ? 'MANUAL / awaiting review' : `Companies House ${sourceIds[i.id]}`}</p>
                        {!uk && <p className="text-amber-300">No usable registry dates. Enter reviewed End/Due dates or leave this service unticked; no default financial year is assumed.</p>}
                        {(evidence.data?.ukRegistry?.warnings ?? []).map((warning) => <p key={warning} className="text-amber-300">{warning}</p>)}
                        {!manualDates[i.id] && sourceIds[i.id] && (sourceIds[i.id] !== evidence.data?.ukRegistry?.companyNumber || ends[i.id] !== uk?.endDate || dates[i.id] !== uk?.dueDate) && <p className="text-amber-300">Registry source changed. Your dates have been kept; review the current source before applying.</p>}
                        {uk && evidence.data?.ukRegistry && <button type="button" className="text-accent underline" onClick={() => { setEnds((state) => ({ ...state, [i.id]: uk.endDate })); setDates((state) => ({ ...state, [i.id]: uk.dueDate })); setManualDates((state) => ({ ...state, [i.id]: false })); setSourceIds((state) => ({ ...state, [i.id]: evidence.data!.ukRegistry!.companyNumber })); setDateRevisions((state) => ({ ...state, [i.id]: (state[i.id] ?? 0) + 1 })); }}>Use reviewed registry dates</button>}
                      </div>}
                    </div>
                    <div className="space-y-2 w-36 shrink-0">
                      {dualDates && <label className="block text-xs text-muted">End date
                        <DateInput key={dateRevisions[i.id] ?? 0} aria-label={`${i.title} End date`} value={ends[i.id] ?? ''} disabled={!checked[i.id]} required={!!ukKind} onChange={(e) => { setEnds((state) => ({ ...state, [i.id]: e.target.value })); setManualDates((state) => ({ ...state, [i.id]: true })); }} className="w-full bg-bg-panel border border-border rounded-lg px-2 py-1 text-xs text-white disabled:opacity-40" />
                      </label>}
                      <label className="block text-xs text-muted">{ukKind === 'hmrcInternalTarget' ? 'Due date (internal target)' : dualDates ? 'Due date (filing)' : 'End date'}
                        <DateInput key={dateRevisions[i.id] ?? 0} aria-label={`${i.title} ${dualDates ? 'Due' : 'End'} date`} value={dates[i.id] ?? ''} disabled={!checked[i.id]} required={dualDates} onChange={(e) => { setDates((d) => ({ ...d, [i.id]: e.target.value })); if (dualDates) setManualDates((state) => ({ ...state, [i.id]: true })); }} className="w-full bg-bg-panel border border-border rounded-lg px-2 py-1 text-xs text-white disabled:opacity-40" />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {apply.error && <p className="text-xs text-rose-400 mt-3">{(apply.error as Error).message}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          {skipLabel && (
            <button
              type="button"
              onClick={onClose}
              className="bg-bg-card hover:bg-bg-hover border border-border text-white text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {skipLabel}
            </button>
          )}
          <button
            onClick={(event) => { if (validateDateInputs(event.currentTarget.closest('[data-date-form]') ?? document)) apply.mutate(); }}
            disabled={!selected || checkedCount === 0 || missingTaxDeadline || apply.isPending}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {apply.isPending ? 'Applying…' : `Apply ${checkedCount > 0 ? checkedCount : ''} ${checkedCount === 1 ? 'item' : 'items'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
